// Angular Core Imports
import { Component, inject, input, OnInit } from '@angular/core';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';

// Third-Party Imports
import { AvatarModule } from 'primeng/avatar';
import { PanelModule } from 'primeng/panel';

// Application-Specific Imports
import { Consumer, OrderPayment } from '@app/resources/services';
import { OrderPaymentStatusTypes } from '@app/@shared/enums';
import { PortalTheme } from '@app/@shared/models';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';
import { formatVisitFrequency } from '@shared/utils';

interface SpendWindow {
  label: string;
  totalSpend: number;
  visits: number;
  averageSpend: number;
  visitFrequency: string;
}

@Component({
  selector: 'app-non-member-information',
  standalone: true,
  imports: [AvatarModule, DatePipe, CurrencyPipe, DecimalPipe, PanelModule],
  templateUrl: './non-member-information.component.html',
  styleUrl: './non-member-information.component.scss',
})
export class NonMemberInformationComponent implements OnInit {
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly cssValidatorService = inject(CssValidatorService);

  nonMember = input.required<Consumer>();

  public firstPurchase = 0;
  public lifetimeWindow: SpendWindow | null = null;

  protected theme: PortalTheme | undefined;

  ngOnInit(): void {
    this.theme = this.applicationProfileService.getApplicationProfile()?.PortalTheme?.Theme;
    this.setCSSCustomProperties();

    const countablePayments = (this.nonMember().OrderPayments ?? []).filter((payment) => !this.isVoided(payment));

    const purchaseTimes = countablePayments.map((payment) => this.toTime(payment.StartDateTime));
    this.firstPurchase = purchaseTimes.length ? Math.min(...purchaseTimes) : 0;

    this.lifetimeWindow = this.toLifetimeWindow();
  }

  private setCSSCustomProperties(): void {
    if (this.theme) {
      // Set CSS custom properties based on the theme
      document.documentElement.style.setProperty('--primary', this.theme.primary);
      // Check for undefined CSS variables
      this.cssValidatorService.checkUndefinedCssVariables('NonMemberInformationComponent');
    }
  }

  private toLifetimeWindow(): SpendWindow | null {
    const lifetimeSpendWindow = this.nonMember().ConsumerRelationship?.SpendWindows.find((window) => !window.Days);

    if (!lifetimeSpendWindow?.Visits) {
      return null;
    }

    return {
      label: 'Lifetime',
      totalSpend: lifetimeSpendWindow.TotalSpend,
      visits: lifetimeSpendWindow.Visits,
      averageSpend: lifetimeSpendWindow.AverageSpend,
      visitFrequency: formatVisitFrequency(lifetimeSpendWindow.AverageDaysBetweenVisits),
    };
  }

  private isVoided(payment: OrderPayment): boolean {
    return (payment.StatusType & OrderPaymentStatusTypes.Voided) === OrderPaymentStatusTypes.Voided;
  }

  private toTime(dateTime: Date | string): number {
    const time = new Date(dateTime).getTime();
    return isNaN(time) ? 0 : time;
  }
}
